import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faSearch, faPlus, faCog, faChartBar, faFilter, faEye, 
    faEdit, faTrash, faShare, faClock, faMapMarkerAlt, 
    faUser, faDollarSign, faCheckCircle, faTimesCircle,
    faExclamationTriangle, faCalendarAlt, faHeartbeat,
    faStethoscope, faXRay, faMicroscope, faThermometerHalf,
    faPlay, faStop
} from '@fortawesome/free-solid-svg-icons';
import DashboardHeader from '../layouts/dashboard_header';
import SideMenu from '../layouts/dashboard_menu';
import DashboardFooter from '../layouts/dashboard_footer';
import { useBreadcrumbs } from '../../utils/utils';
import localForage from 'localforage';
import Swal from 'sweetalert2';
import axios from 'axios';

// Types
interface EquipmentCategory {
    id: number;
    name: string;
    description: string;
    icon: string;
    equipment_count: number;
}

interface MedicalEquipment {
    equipment_id: string;
    name: string;
    category: EquipmentCategory;
    manufacturer: string;
    model: string;
    serial_number: string;
    location: string;
    status: string;
    is_shareable: boolean;
    sharing_enabled: boolean;
    specifications: any;
    capabilities: string[];
    supported_measurements: string[];
    sharing_profile?: {
        available_terms: string[];
        default_term: string;
        hourly_rate?: number;
        daily_rate?: number;
        weekly_rate?: number;
        monthly_rate?: number;
        requires_approval: boolean;
        requires_training: boolean;
        max_concurrent_users: number;
    };
    created_at: string;
    last_maintenance?: string;
    owner_partner?: {
        id: number;
        name: string;
    };
}

interface EquipmentRequest {
    request_id: string;
    equipment: {
        equipment_id: string;
        name: string;
        manufacturer: string;
        model: string;
    };
    patient: {
        id: number;
        name: string;
    };
    requesting_partner: {
        id: number;
        name: string;
    };
    term_of_engagement: string;
    requested_start_date: string;
    requested_end_date: string;
    estimated_duration_hours: number;
    status: string;
    priority: string;
    patient_condition: string;
    medical_justification: string;
    approved_by?: {
        id: number;
        name: string;
    };
    approved_at?: string;
    approval_notes: string;
    agreed_rate?: number;
    total_cost?: number;
    created_at: string;
}

interface EquipmentStats {
    owned_equipment: number;
    shareable_equipment: number;
    sent_requests: number;
    approved_requests: number;
    received_requests: number;
    pending_requests: number;
    active_collaborations: number;
    total_usage_hours: number;
    average_success_rate: number;
}

const UzimaEquipment: React.FC = () => {
    // State management
    const [activeTab, setActiveTab] = useState<'overview' | 'my-equipment' | 'find-equipment' | 'requests' | 'collaborations'>('overview');
    const [loading, setLoading] = useState(false);
    const [partnerId, setPartnerId] = useState<number | null>(null);
    const [clinicName, setClinicName] = useState('');
    const [fname, setFname] = useState('');
    
    // Data states
    const [categories, setCategories] = useState<EquipmentCategory[]>([]);
    const [myEquipment, setMyEquipment] = useState<MedicalEquipment[]>([]);
    const [availableEquipment, setAvailableEquipment] = useState<MedicalEquipment[]>([]);
    const [equipmentRequests, setEquipmentRequests] = useState<EquipmentRequest[]>([]);
    const [stats, setStats] = useState<EquipmentStats | null>(null);
    
    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
    const [selectedTerm, setSelectedTerm] = useState<string>('');
    const [requestFilter, setRequestFilter] = useState<'all' | 'sent' | 'received'>('all');
    
    // Modal states
    const [showAddEquipment, setShowAddEquipment] = useState(false);
    const [showRequestModal, setShowRequestModal] = useState(false);
    const [selectedEquipment, setSelectedEquipment] = useState<MedicalEquipment | null>(null);

    useEffect(() => {
        document.title = "Equipment Collaboration - Uzima Healthcare";
        loadUserData();
    }, []);

    useEffect(() => {
        if (partnerId) {
            loadInitialData();
        }
    }, [partnerId]);

    const loadUserData = async () => {
        try {
            const storedPartnerId = await localForage.getItem('partner_id');
            const storedClinicName = await localForage.getItem('clinic_name');
            const storedFname = await localForage.getItem('fname');
            
            if (storedPartnerId) setPartnerId(Number(storedPartnerId));
            if (storedClinicName) setClinicName(String(storedClinicName));
            if (storedFname) setFname(String(storedFname));
        } catch (error) {
            console.error('Error loading user data:', error);
        }
    };

    const loadInitialData = async () => {
        setLoading(true);
        try {
            await Promise.all([
                loadCategories(),
                loadMyEquipment(),
                loadAvailableEquipment(),
                loadEquipmentRequests(),
                loadStats()
            ]);
        } catch (error) {
            console.error('Error loading initial data:', error);
            Swal.fire('Error', 'Failed to load equipment data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const response = await axios.get('/collaborations/equipment/categories/');
            if (response.data.success) {
                setCategories(response.data.data);
            }
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    };

    const loadMyEquipment = async () => {
        if (!partnerId) return;
        try {
            const response = await axios.get(`/collaborations/equipment/partner-equipment/?partner_id=${partnerId}`);
            if (response.data.success) {
                setMyEquipment(response.data.data);
            }
        } catch (error) {
            console.error('Error loading my equipment:', error);
        }
    };

    const loadAvailableEquipment = async () => {
        try {
            const params = new URLSearchParams();
            if (selectedCategory) params.append('category_id', selectedCategory.toString());
            if (selectedTerm) params.append('term_type', selectedTerm);
            if (searchTerm) params.append('search', searchTerm);
            
            const response = await axios.get(`/collaborations/equipment/available/?${params}`);
            if (response.data.success) {
                setAvailableEquipment(response.data.data);
            }
        } catch (error) {
            console.error('Error loading available equipment:', error);
        }
    };

    const loadEquipmentRequests = async () => {
        if (!partnerId) return;
        try {
            const response = await axios.get(`/collaborations/equipment/requests/?partner_id=${partnerId}&type=${requestFilter}`);
            if (response.data.success) {
                setEquipmentRequests(response.data.data);
            }
        } catch (error) {
            console.error('Error loading equipment requests:', error);
        }
    };

    const loadStats = async () => {
        if (!partnerId) return;
        try {
            const response = await axios.get(`/collaborations/equipment/stats/?partner_id=${partnerId}`);
            if (response.data.success) {
                setStats(response.data.data);
            }
        } catch (error) {
            console.error('Error loading stats:', error);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'available': return 'success';
            case 'in_use': return 'warning';
            case 'maintenance': return 'info';
            case 'offline': return 'danger';
            case 'pending': return 'warning';
            case 'approved': return 'success';
            case 'rejected': return 'danger';
            case 'in_progress': return 'primary';
            case 'completed': return 'success';
            default: return 'secondary';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'available': return faCheckCircle;
            case 'in_use': return faClock;
            case 'maintenance': return faCog;
            case 'offline': return faTimesCircle;
            case 'pending': return faClock;
            case 'approved': return faCheckCircle;
            case 'rejected': return faTimesCircle;
            case 'in_progress': return faClock;
            case 'completed': return faCheckCircle;
            default: return faExclamationTriangle;
        }
    };

    const getCategoryIcon = (categoryName: string) => {
        switch (categoryName.toLowerCase()) {
            case 'monitoring': return faHeartbeat;
            case 'diagnostic': return faStethoscope;
            case 'imaging': return faXRay;
            case 'laboratory': return faMicroscope;
            case 'vital signs': return faThermometerHalf;
            default: return faCog;
        }
    };

    const formatCurrency = (amount?: number) => {
        if (!amount) return 'N/A';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleRequestEquipment = (equipment: MedicalEquipment) => {
        setSelectedEquipment(equipment);
        setShowRequestModal(true);
    };

    const handleToggleSharing = async (equipment: MedicalEquipment) => {
        try {
            const newSharingEnabled = !equipment.sharing_enabled;
            await axios.put(`/collaborations/equipment/${equipment.equipment_id}/update/`, {
                sharing_enabled: newSharingEnabled
            });
            
            Swal.fire('Success', `Equipment sharing ${newSharingEnabled ? 'enabled' : 'disabled'}`, 'success');
            loadMyEquipment();
        } catch (error) {
            console.error('Error toggling sharing:', error);
            Swal.fire('Error', 'Failed to update equipment sharing', 'error');
        }
    };

    const handleApproveRequest = async (requestId: string, approved: boolean) => {
        try {
            await axios.put(`/collaborations/equipment/requests/${requestId}/status/`, {
                status: approved ? 'approved' : 'rejected',
                approval_notes: approved ? 'Request approved' : 'Request rejected'
            });
            
            Swal.fire('Success', `Request ${approved ? 'approved' : 'rejected'}`, 'success');
            loadEquipmentRequests();
            loadStats();
        } catch (error) {
            console.error('Error updating request status:', error);
            Swal.fire('Error', 'Failed to update request status', 'error');
        }
    };

    return (
        <>
            <DashboardHeader />
            <SideMenu />

            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        {useBreadcrumbs(clinicName, "Equipment Collaboration", "Equipment", "Launch")}

                        {/* Header */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <h2 className="fw-bold mb-1">
                                    <FontAwesomeIcon icon={faCog} className="me-2 text-primary" />
                                    Equipment Collaboration Center
                                </h2>
                                <p className="text-muted mb-0">
                                    Share and access medical equipment across partner networks
                                </p>
                            </div>
                        </div>

                        {/* Navigation Tabs */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-body">
                                        <ul className="nav nav-pills nav-justified" role="tablist">
                                            <li className="nav-item">
                                                <button 
                                                    className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                                                    onClick={() => setActiveTab('overview')}
                                                >
                                                    <FontAwesomeIcon icon={faChartBar} className="me-2" />
                                                    Overview
                                                </button>
                                            </li>
                                            <li className="nav-item">
                                                <button 
                                                    className={`nav-link ${activeTab === 'my-equipment' ? 'active' : ''}`}
                                                    onClick={() => setActiveTab('my-equipment')}
                                                >
                                                    <FontAwesomeIcon icon={faCog} className="me-2" />
                                                    My Equipment
                                                </button>
                                            </li>
                                            <li className="nav-item">
                                                <button 
                                                    className={`nav-link ${activeTab === 'find-equipment' ? 'active' : ''}`}
                                                    onClick={() => setActiveTab('find-equipment')}
                                                >
                                                    <FontAwesomeIcon icon={faSearch} className="me-2" />
                                                    Find Equipment
                                                </button>
                                            </li>
                                            <li className="nav-item">
                                                <button 
                                                    className={`nav-link ${activeTab === 'requests' ? 'active' : ''}`}
                                                    onClick={() => setActiveTab('requests')}
                                                >
                                                    <FontAwesomeIcon icon={faClock} className="me-2" />
                                                    Requests
                                                </button>
                                            </li>
                                            <li className="nav-item">
                                                <button 
                                                    className={`nav-link ${activeTab === 'collaborations' ? 'active' : ''}`}
                                                    onClick={() => setActiveTab('collaborations')}
                                                >
                                                    <FontAwesomeIcon icon={faShare} className="me-2" />
                                                    Collaborations
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Tab Content */}
                        {activeTab === 'overview' && (
                            <div className="row">
                                {/* Stats Cards */}
                                <div className="col-xl-3 col-md-6">
                                    <div className="card card-animate">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center">
                                                <div className="flex-grow-1 overflow-hidden">
                                                    <p className="text-uppercase fw-medium text-muted text-truncate mb-0">My Equipment</p>
                                                </div>
                                                <div className="flex-shrink-0">
                                                    <h5 className="text-success fs-14 mb-0">
                                                        <FontAwesomeIcon icon={faCog} className="me-1" />
                                                    </h5>
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-end justify-content-between mt-4">
                                                <div>
                                                    <h4 className="fs-22 fw-semibold ff-secondary mb-4">
                                                        <span className="counter-value" data-target={stats?.owned_equipment || 0}>
                                                            {stats?.owned_equipment || 0}
                                                        </span>
                                                    </h4>
                                                    <span className="badge bg-success-subtle text-success mb-0">
                                                        <FontAwesomeIcon icon={faCheckCircle} className="align-middle me-1" />
                                                        {stats?.shareable_equipment || 0} Shareable
                                                    </span>
                                                </div>
                                                <div className="avatar-sm flex-shrink-0">
                                                    <span className="avatar-title bg-success-subtle rounded fs-3">
                                                        <FontAwesomeIcon icon={faCog} className="text-success" />
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-xl-3 col-md-6">
                                    <div className="card card-animate">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center">
                                                <div className="flex-grow-1 overflow-hidden">
                                                    <p className="text-uppercase fw-medium text-muted text-truncate mb-0">Active Requests</p>
                                                </div>
                                                <div className="flex-shrink-0">
                                                    <h5 className="text-warning fs-14 mb-0">
                                                        <FontAwesomeIcon icon={faClock} className="me-1" />
                                                    </h5>
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-end justify-content-between mt-4">
                                                <div>
                                                    <h4 className="fs-22 fw-semibold ff-secondary mb-4">
                                                        <span className="counter-value" data-target={stats?.sent_requests || 0}>
                                                            {stats?.sent_requests || 0}
                                                        </span>
                                                    </h4>
                                                    <span className="badge bg-warning-subtle text-warning mb-0">
                                                        <FontAwesomeIcon icon={faExclamationTriangle} className="align-middle me-1" />
                                                        {stats?.pending_requests || 0} Pending
                                                    </span>
                                                </div>
                                                <div className="avatar-sm flex-shrink-0">
                                                    <span className="avatar-title bg-warning-subtle rounded fs-3">
                                                        <FontAwesomeIcon icon={faClock} className="text-warning" />
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-xl-3 col-md-6">
                                    <div className="card card-animate">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center">
                                                <div className="flex-grow-1 overflow-hidden">
                                                    <p className="text-uppercase fw-medium text-muted text-truncate mb-0">Collaborations</p>
                                                </div>
                                                <div className="flex-shrink-0">
                                                    <h5 className="text-info fs-14 mb-0">
                                                        <FontAwesomeIcon icon={faShare} className="me-1" />
                                                    </h5>
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-end justify-content-between mt-4">
                                                <div>
                                                    <h4 className="fs-22 fw-semibold ff-secondary mb-4">
                                                        <span className="counter-value" data-target={stats?.active_collaborations || 0}>
                                                            {stats?.active_collaborations || 0}
                                                        </span>
                                                    </h4>
                                                    <span className="badge bg-info-subtle text-info mb-0">
                                                        <FontAwesomeIcon icon={faChartBar} className="align-middle me-1" />
                                                        {stats?.total_usage_hours || 0}h Total
                                                    </span>
                                                </div>
                                                <div className="avatar-sm flex-shrink-0">
                                                    <span className="avatar-title bg-info-subtle rounded fs-3">
                                                        <FontAwesomeIcon icon={faShare} className="text-info" />
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-xl-3 col-md-6">
                                    <div className="card card-animate">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center">
                                                <div className="flex-grow-1 overflow-hidden">
                                                    <p className="text-uppercase fw-medium text-muted text-truncate mb-0">Success Rate</p>
                                                </div>
                                                <div className="flex-shrink-0">
                                                    <h5 className="text-primary fs-14 mb-0">
                                                        <FontAwesomeIcon icon={faChartBar} className="me-1" />
                                                    </h5>
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-end justify-content-between mt-4">
                                                <div>
                                                    <h4 className="fs-22 fw-semibold ff-secondary mb-4">
                                                        <span className="counter-value" data-target={stats?.average_success_rate || 0}>
                                                            {Math.round((stats?.average_success_rate || 0) * 100)}%
                                                        </span>
                                                    </h4>
                                                    <span className="badge bg-primary-subtle text-primary mb-0">
                                                        <FontAwesomeIcon icon={faCheckCircle} className="align-middle me-1" />
                                                        Average
                                                    </span>
                                                </div>
                                                <div className="avatar-sm flex-shrink-0">
                                                    <span className="avatar-title bg-primary-subtle rounded fs-3">
                                                        <FontAwesomeIcon icon={faChartBar} className="text-primary" />
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Quick Actions */}
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-header">
                                            <h4 className="card-title mb-0">Quick Actions</h4>
                                        </div>
                                        <div className="card-body">
                                            <div className="row g-3">
                                                <div className="col-md-3">
                                                    <button 
                                                        className="btn btn-primary w-100"
                                                        onClick={() => setActiveTab('my-equipment')}
                                                    >
                                                        <FontAwesomeIcon icon={faCog} className="me-2" />
                                                        Manage My Equipment
                                                    </button>
                                                </div>
                                                <div className="col-md-3">
                                                    <button 
                                                        className="btn btn-success w-100"
                                                        onClick={() => setActiveTab('find-equipment')}
                                                    >
                                                        <FontAwesomeIcon icon={faSearch} className="me-2" />
                                                        Find Equipment
                                                    </button>
                                                </div>
                                                <div className="col-md-3">
                                                    <button 
                                                        className="btn btn-info w-100"
                                                        onClick={() => setActiveTab('requests')}
                                                    >
                                                        <FontAwesomeIcon icon={faClock} className="me-2" />
                                                        View Requests
                                                    </button>
                                                </div>
                                                <div className="col-md-3">
                                                    <button 
                                                        className="btn btn-warning w-100"
                                                        onClick={() => setActiveTab('collaborations')}
                                                    >
                                                        <FontAwesomeIcon icon={faShare} className="me-2" />
                                                        Collaborations
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'my-equipment' && (
                            <div className="row">
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-header d-flex justify-content-between align-items-center">
                                            <h4 className="card-title mb-0">My Equipment</h4>
                                            <button 
                                                className="btn btn-primary"
                                                onClick={() => setShowAddEquipment(true)}
                                            >
                                                <FontAwesomeIcon icon={faPlus} className="me-2" />
                                                Add Equipment
                                            </button>
                                        </div>
                                        <div className="card-body">
                                            {loading ? (
                                                <div className="text-center py-4">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                </div>
                                            ) : myEquipment.length === 0 ? (
                                                <div className="text-center py-5">
                                                    <FontAwesomeIcon icon={faCog} className="text-muted fs-1 mb-3" />
                                                    <h5 className="text-muted">No equipment found</h5>
                                                    <p className="text-muted">Add your first piece of medical equipment to get started</p>
                                                    <button 
                                                        className="btn btn-primary"
                                                        onClick={() => setShowAddEquipment(true)}
                                                    >
                                                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                                                        Add Equipment
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="table-responsive">
                                                    <table className="table table-hover">
                                                        <thead>
                                                            <tr>
                                                                <th>Equipment</th>
                                                                <th>Category</th>
                                                                <th>Location</th>
                                                                <th>Status</th>
                                                                <th>Sharing</th>
                                                                <th>Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {myEquipment.map((equipment) => (
                                                                <tr key={equipment.equipment_id}>
                                                                    <td>
                                                                        <div className="d-flex align-items-center">
                                                                            <div className="avatar-sm me-3">
                                                                                <span className="avatar-title bg-primary-subtle rounded">
                                                                                    <FontAwesomeIcon 
                                                                                        icon={getCategoryIcon(equipment.category.name)} 
                                                                                        className="text-primary" 
                                                                                    />
                                                                                </span>
                                                                            </div>
                                                                            <div>
                                                                                <h6 className="mb-1">{equipment.name}</h6>
                                                                                <small className="text-muted">
                                                                                    {equipment.manufacturer} {equipment.model}
                                                                                </small>
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <span className="badge bg-secondary-subtle text-secondary">
                                                                            {equipment.category.name}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <div className="d-flex align-items-center">
                                                                            <FontAwesomeIcon icon={faMapMarkerAlt} className="text-muted me-1" />
                                                                            {equipment.location}
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <span className={`badge bg-${getStatusColor(equipment.status)}-subtle text-${getStatusColor(equipment.status)}`}>
                                                                            <FontAwesomeIcon 
                                                                                icon={getStatusIcon(equipment.status)} 
                                                                                className="me-1" 
                                                                            />
                                                                            {equipment.status.replace('_', ' ').toUpperCase()}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <div className="form-check form-switch">
                                                                            <input 
                                                                                className="form-check-input" 
                                                                                type="checkbox" 
                                                                                checked={equipment.sharing_enabled}
                                                                                onChange={() => handleToggleSharing(equipment)}
                                                                                disabled={!equipment.is_shareable}
                                                                            />
                                                                            <label className="form-check-label">
                                                                                {equipment.sharing_enabled ? 'Enabled' : 'Disabled'}
                                                                            </label>
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <div className="d-flex gap-2">
                                                                            <button className="btn btn-sm btn-outline-primary">
                                                                                <FontAwesomeIcon icon={faEye} />
                                                                            </button>
                                                                            <button className="btn btn-sm btn-outline-secondary">
                                                                                <FontAwesomeIcon icon={faEdit} />
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'find-equipment' && (
                            <div className="row">
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-header">
                                            <h4 className="card-title mb-0">Find Available Equipment</h4>
                                        </div>
                                        <div className="card-body">
                                            {/* Search and Filters */}
                                            <div className="row mb-4">
                                                <div className="col-md-4">
                                                    <div className="input-group">
                                                        <span className="input-group-text">
                                                            <FontAwesomeIcon icon={faSearch} />
                                                        </span>
                                                        <input 
                                                            type="text" 
                                                            className="form-control" 
                                                            placeholder="Search equipment..."
                                                            value={searchTerm}
                                                            onChange={(e) => setSearchTerm(e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-3">
                                                    <select 
                                                        className="form-select"
                                                        value={selectedCategory || ''}
                                                        onChange={(e) => setSelectedCategory(e.target.value ? Number(e.target.value) : null)}
                                                    >
                                                        <option value="">All Categories</option>
                                                        {categories.map((category) => (
                                                            <option key={category.id} value={category.id}>
                                                                {category.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-md-3">
                                                    <select 
                                                        className="form-select"
                                                        value={selectedTerm}
                                                        onChange={(e) => setSelectedTerm(e.target.value)}
                                                    >
                                                        <option value="">All Terms</option>
                                                        <option value="once">One-time Use</option>
                                                        <option value="short_term">Short Term (1-7 days)</option>
                                                        <option value="medium_term">Medium Term (1-4 weeks)</option>
                                                        <option value="long_term">Long Term (1-6 months)</option>
                                                        <option value="permanent">Permanent Access</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-2">
                                                    <button 
                                                        className="btn btn-primary w-100"
                                                        onClick={loadAvailableEquipment}
                                                    >
                                                        <FontAwesomeIcon icon={faSearch} className="me-1" />
                                                        Search
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Equipment Grid */}
                                            {loading ? (
                                                <div className="text-center py-4">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                </div>
                                            ) : availableEquipment.length === 0 ? (
                                                <div className="text-center py-5">
                                                    <FontAwesomeIcon icon={faSearch} className="text-muted fs-1 mb-3" />
                                                    <h5 className="text-muted">No equipment found</h5>
                                                    <p className="text-muted">Try adjusting your search criteria</p>
                                                </div>
                                            ) : (
                                                <div className="row">
                                                    {availableEquipment.map((equipment) => (
                                                        <div key={equipment.equipment_id} className="col-md-6 col-lg-4 mb-4">
                                                            <div className="card h-100">
                                                                <div className="card-body">
                                                                    <div className="d-flex align-items-start mb-3">
                                                                        <div className="avatar-sm me-3">
                                                                            <span className="avatar-title bg-primary-subtle rounded">
                                                                                <FontAwesomeIcon 
                                                                                    icon={getCategoryIcon(equipment.category.name)} 
                                                                                    className="text-primary" 
                                                                                />
                                                                            </span>
                                                                        </div>
                                                                        <div className="flex-grow-1">
                                                                            <h6 className="card-title mb-1">{equipment.name}</h6>
                                                                            <small className="text-muted">
                                                                                {equipment.manufacturer} {equipment.model}
                                                                            </small>
                                                                        </div>
                                                                    </div>
                                                                    
                                                                    <div className="mb-3">
                                                                        <span className="badge bg-secondary-subtle text-secondary me-2">
                                                                            {equipment.category.name}
                                                                        </span>
                                                                        <span className={`badge bg-${getStatusColor(equipment.status)}-subtle text-${getStatusColor(equipment.status)}`}>
                                                                            <FontAwesomeIcon 
                                                                                icon={getStatusIcon(equipment.status)} 
                                                                                className="me-1" 
                                                                            />
                                                                            {equipment.status.replace('_', ' ').toUpperCase()}
                                                                        </span>
                                                                    </div>

                                                                    <div className="mb-3">
                                                                        <div className="d-flex align-items-center mb-1">
                                                                            <FontAwesomeIcon icon={faMapMarkerAlt} className="text-muted me-2" />
                                                                            <small>{equipment.location}</small>
                                                                        </div>
                                                                        <div className="d-flex align-items-center mb-1">
                                                                            <FontAwesomeIcon icon={faUser} className="text-muted me-2" />
                                                                            <small>{equipment.owner_partner?.name}</small>
                                                                        </div>
                                                                    </div>

                                                                    {equipment.sharing_profile && (
                                                                        <div className="mb-3">
                                                                            <h6 className="small mb-2">Pricing:</h6>
                                                                            <div className="row g-2">
                                                                                {equipment.sharing_profile.hourly_rate && (
                                                                                    <div className="col-6">
                                                                                        <small className="text-muted">Hourly:</small>
                                                                                        <div className="fw-medium">{formatCurrency(equipment.sharing_profile.hourly_rate)}</div>
                                                                                    </div>
                                                                                )}
                                                                                {equipment.sharing_profile.daily_rate && (
                                                                                    <div className="col-6">
                                                                                        <small className="text-muted">Daily:</small>
                                                                                        <div className="fw-medium">{formatCurrency(equipment.sharing_profile.daily_rate)}</div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    <div className="d-flex gap-2">
                                                                        <button 
                                                                            className="btn btn-primary btn-sm flex-grow-1"
                                                                            onClick={() => handleRequestEquipment(equipment)}
                                                                        >
                                                                            <FontAwesomeIcon icon={faClock} className="me-1" />
                                                                            Request
                                                                        </button>
                                                                        <button className="btn btn-outline-secondary btn-sm">
                                                                            <FontAwesomeIcon icon={faEye} />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'requests' && (
                            <div className="row">
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-header d-flex justify-content-between align-items-center">
                                            <h4 className="card-title mb-0">Equipment Requests</h4>
                                            <div className="btn-group" role="group">
                                                <button 
                                                    className={`btn ${requestFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                    onClick={() => setRequestFilter('all')}
                                                >
                                                    All
                                                </button>
                                                <button 
                                                    className={`btn ${requestFilter === 'sent' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                    onClick={() => setRequestFilter('sent')}
                                                >
                                                    Sent
                                                </button>
                                                <button 
                                                    className={`btn ${requestFilter === 'received' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                    onClick={() => setRequestFilter('received')}
                                                >
                                                    Received
                                                </button>
                                            </div>
                                        </div>
                                        <div className="card-body">
                                            {loading ? (
                                                <div className="text-center py-4">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                </div>
                                            ) : equipmentRequests.length === 0 ? (
                                                <div className="text-center py-5">
                                                    <FontAwesomeIcon icon={faClock} className="text-muted fs-1 mb-3" />
                                                    <h5 className="text-muted">No requests found</h5>
                                                    <p className="text-muted">Equipment requests will appear here</p>
                                                </div>
                                            ) : (
                                                <div className="table-responsive">
                                                    <table className="table table-hover">
                                                        <thead>
                                                            <tr>
                                                                <th>Equipment</th>
                                                                <th>Patient</th>
                                                                <th>Requested By</th>
                                                                <th>Term</th>
                                                                <th>Duration</th>
                                                                <th>Status</th>
                                                                <th>Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {equipmentRequests.map((request) => (
                                                                <tr key={request.request_id}>
                                                                    <td>
                                                                        <div>
                                                                            <h6 className="mb-1">{request.equipment.name}</h6>
                                                                            <small className="text-muted">
                                                                                {request.equipment.manufacturer} {request.equipment.model}
                                                                            </small>
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <div className="d-flex align-items-center">
                                                                            <div className="avatar-sm me-2">
                                                                                <span className="avatar-title bg-primary-subtle rounded">
                                                                                    <FontAwesomeIcon icon={faUser} className="text-primary" />
                                                                                </span>
                                                                            </div>
                                                                            <div>
                                                                                <h6 className="mb-0">{request.patient.name}</h6>
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <small>{request.requesting_partner.name}</small>
                                                                    </td>
                                                                    <td>
                                                                        <span className="badge bg-info-subtle text-info">
                                                                            {request.term_of_engagement.replace('_', ' ').toUpperCase()}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <div>
                                                                            <small className="text-muted">Start:</small>
                                                                            <div className="small">{formatDate(request.requested_start_date)}</div>
                                                                            <small className="text-muted">Duration:</small>
                                                                            <div className="small">{request.estimated_duration_hours}h</div>
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <span className={`badge bg-${getStatusColor(request.status)}-subtle text-${getStatusColor(request.status)}`}>
                                                                            <FontAwesomeIcon 
                                                                                icon={getStatusIcon(request.status)} 
                                                                                className="me-1" 
                                                                            />
                                                                            {request.status.replace('_', ' ').toUpperCase()}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        {request.status === 'pending' && requestFilter === 'received' && (
                                                                            <div className="d-flex gap-1">
                                                                                <button 
                                                                                    className="btn btn-sm btn-success"
                                                                                    onClick={() => handleApproveRequest(request.request_id, true)}
                                                                                >
                                                                                    <FontAwesomeIcon icon={faCheckCircle} />
                                                                                </button>
                                                                                <button 
                                                                                    className="btn btn-sm btn-danger"
                                                                                    onClick={() => handleApproveRequest(request.request_id, false)}
                                                                                >
                                                                                    <FontAwesomeIcon icon={faTimesCircle} />
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                        <button className="btn btn-sm btn-outline-primary">
                                                                            <FontAwesomeIcon icon={faEye} />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'collaborations' && (
                            <div className="row">
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-header d-flex justify-content-between align-items-center">
                                            <h4 className="card-title mb-0">Active Collaborations</h4>
                                            <div className="btn-group" role="group">
                                                <button className="btn btn-outline-primary btn-sm">
                                                    <FontAwesomeIcon icon={faFilter} className="me-1" />
                                                    Filter
                                                </button>
                                                <button className="btn btn-outline-secondary btn-sm">
                                                    <FontAwesomeIcon icon={faChartBar} className="me-1" />
                                                    Analytics
                                                </button>
                                            </div>
                                        </div>
                                        <div className="card-body">
                                            {loading ? (
                                                <div className="text-center py-4">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="row">
                                                        <div className="col-md-3 mb-4">
                                                            <div className="card border-primary">
                                                                <div className="card-body text-center">
                                                                    <FontAwesomeIcon icon={faShare} className="text-primary fs-1 mb-2" />
                                                                    <h5 className="card-title">Active Collaborations</h5>
                                                                    <h3 className="text-primary">{stats?.active_collaborations || 0}</h3>
                                                                    <small className="text-muted">Currently ongoing</small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="col-md-3 mb-4">
                                                            <div className="card border-success">
                                                                <div className="card-body text-center">
                                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-success fs-1 mb-2" />
                                                                    <h5 className="card-title">Completed</h5>
                                                                    <h3 className="text-success">{stats?.approved_requests || 0}</h3>
                                                                    <small className="text-muted">Successfully completed</small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="col-md-3 mb-4">
                                                            <div className="card border-info">
                                                                <div className="card-body text-center">
                                                                    <FontAwesomeIcon icon={faClock} className="text-info fs-1 mb-2" />
                                                                    <h5 className="card-title">Total Hours</h5>
                                                                    <h3 className="text-info">{stats?.total_usage_hours || 0}</h3>
                                                                    <small className="text-muted">Equipment usage time</small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="col-md-3 mb-4">
                                                            <div className="card border-warning">
                                                                <div className="card-body text-center">
                                                                    <FontAwesomeIcon icon={faChartBar} className="text-warning fs-1 mb-2" />
                                                                    <h5 className="card-title">Success Rate</h5>
                                                                    <h3 className="text-warning">{Math.round((stats?.average_success_rate || 0) * 100)}%</h3>
                                                                    <small className="text-muted">Overall performance</small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="row mt-4">
                                                    <div className="col-12">
                                                        <h5 className="mb-3">Recent Collaborations</h5>
                                                        <div className="table-responsive">
                                                            <table className="table table-hover">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Equipment</th>
                                                                        <th>Partner</th>
                                                                        <th>Patient</th>
                                                                        <th>Duration</th>
                                                                        <th>Status</th>
                                                                        <th>Actions</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {equipmentRequests.filter(req => 
                                                                        ['approved', 'in_progress', 'completed'].includes(req.status)
                                                                    ).slice(0, 5).map((request) => (
                                                                        <tr key={request.request_id}>
                                                                            <td>
                                                                                <div className="d-flex align-items-center">
                                                                                    <div className="avatar-sm me-3">
                                                                                        <span className="avatar-title bg-primary-subtle rounded">
                                                                                            <FontAwesomeIcon 
                                                                                                icon={getCategoryIcon(request.equipment.name)} 
                                                                                                className="text-primary" 
                                                                                            />
                                                                                        </span>
                                                                                    </div>
                                                                                    <div>
                                                                                        <h6 className="mb-1">{request.equipment.name}</h6>
                                                                                        <small className="text-muted">
                                                                                            {request.equipment.manufacturer} {request.equipment.model}
                                                                                        </small>
                                                                                    </div>
                                                                                </div>
                                                                            </td>
                                                                            <td>
                                                                                <div className="d-flex align-items-center">
                                                                                    <div className="avatar-sm me-2">
                                                                                        <span className="avatar-title bg-secondary-subtle rounded">
                                                                                            <FontAwesomeIcon icon={faUser} className="text-secondary" />
                                                                                        </span>
                                                                                    </div>
                                                                                    <div>
                                                                                        <h6 className="mb-0">{request.requesting_partner.name}</h6>
                                                                                    </div>
                                                                                </div>
                                                                            </td>
                                                                            <td>
                                                                                <div className="d-flex align-items-center">
                                                                                    <div className="avatar-sm me-2">
                                                                                        <span className="avatar-title bg-info-subtle rounded">
                                                                                            <FontAwesomeIcon icon={faUser} className="text-info" />
                                                                                        </span>
                                                                                    </div>
                                                                                    <div>
                                                                                        <h6 className="mb-0">{request.patient.name}</h6>
                                                                                    </div>
                                                                                </div>
                                                                            </td>
                                                                            <td>
                                                                                <div>
                                                                                    <small className="text-muted">Duration:</small>
                                                                                    <div className="fw-medium">{request.estimated_duration_hours}h</div>
                                                                                    <small className="text-muted">Term: {request.term_of_engagement.replace('_', ' ')}</small>
                                                                                </div>
                                                                            </td>
                                                                            <td>
                                                                                <span className={`badge bg-${getStatusColor(request.status)}-subtle text-${getStatusColor(request.status)}`}>
                                                                                    <FontAwesomeIcon 
                                                                                        icon={getStatusIcon(request.status)} 
                                                                                        className="me-1" 
                                                                                    />
                                                                                    {request.status.replace('_', ' ').toUpperCase()}
                                                                                </span>
                                                                            </td>
                                                                            <td>
                                                                                <div className="d-flex gap-1">
                                                                                    <button className="btn btn-sm btn-outline-primary">
                                                                                        <FontAwesomeIcon icon={faEye} />
                                                                                    </button>
                                                                                    {request.status === 'approved' && (
                                                                                        <button className="btn btn-sm btn-success">
                                                                                            <FontAwesomeIcon icon={faPlay} />
                                                                                        </button>
                                                                                    )}
                                                                                    {request.status === 'in_progress' && (
                                                                                        <button className="btn btn-sm btn-warning">
                                                                                            <FontAwesomeIcon icon={faStop} />
                                                                                        </button>
                                                                                    )}
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                        
                                                        {equipmentRequests.filter(req => 
                                                            ['approved', 'in_progress', 'completed'].includes(req.status)
                                                        ).length === 0 && (
                                                            <div className="text-center py-5">
                                                                <FontAwesomeIcon icon={faShare} className="text-muted fs-1 mb-3" />
                                                                <h5 className="text-muted">No Active Collaborations</h5>
                                                                <p className="text-muted">Your equipment collaborations will appear here once requests are approved</p>
                                                                <button 
                                                                    className="btn btn-primary"
                                                                    onClick={() => setActiveTab('requests')}
                                                                >
                                                                    <FontAwesomeIcon icon={faClock} className="me-2" />
                                                                    View Requests
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                <div className="row mt-4">
                                                    <div className="col-12">
                                                        <h5 className="mb-3">Collaboration Timeline</h5>
                                                        <div className="card">
                                                            <div className="card-body">
                                                                <div className="timeline" style={{
                                                                    position: 'relative',
                                                                    paddingLeft: '30px'
                                                                }}>
                                                                    {equipmentRequests.filter(req => 
                                                                        ['approved', 'in_progress', 'completed'].includes(req.status)
                                                                    ).slice(0, 3).map((request, index) => (
                                                                        <div key={request.request_id} className="timeline-item" style={{
                                                                            position: 'relative',
                                                                            marginBottom: '20px'
                                                                        }}>
                                                                            <div className="timeline-marker" style={{
                                                                                position: 'absolute',
                                                                                left: '-35px',
                                                                                top: '5px',
                                                                                width: '20px',
                                                                                height: '20px',
                                                                                borderRadius: '50%',
                                                                                backgroundColor: '#fff',
                                                                                border: `2px solid var(--bs-${getStatusColor(request.status)})`,
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                fontSize: '10px'
                                                                            }}>
                                                                                <FontAwesomeIcon 
                                                                                    icon={getStatusIcon(request.status)} 
                                                                                    className={`text-${getStatusColor(request.status)}`}
                                                                                />
                                                                            </div>
                                                                            <div className="timeline-content" style={{
                                                                                backgroundColor: '#f8f9fa',
                                                                                padding: '15px',
                                                                                borderRadius: '8px',
                                                                                borderLeft: `3px solid var(--bs-${getStatusColor(request.status)})`
                                                                            }}>
                                                                                <h6 className="mb-1">
                                                                                    {request.equipment.name} - {request.status.replace('_', ' ').toUpperCase()}
                                                                                </h6>
                                                                                <p className="text-muted mb-1">
                                                                                    {request.requesting_partner.name} requested {request.equipment.name} for patient {request.patient.name}
                                                                                </p>
                                                                                <small className="text-muted">
                                                                                    {formatDate(request.created_at)} • {request.estimated_duration_hours}h duration
                                                                                </small>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <DashboardFooter />
            </div>
        </>
    );
};

export default UzimaEquipment;
